<template>
  <!--
    WHAT: Field-mapping editor for a metric insight's visualization. Per viz_type
          (chart_v5, table, sankey, heatmap, pie) it maps the insight's real
          output columns into the exact viz_config schema metricInsightAdapter.js
          expects, plus layout (width / display order) and an Advanced raw
          chart_config JSON passthrough. For task insights it shows an
          explanatory note + insight_type toggle instead (no chart).
    WHERE: [reused verbatim from v1] Wrapped by InsightVizSection.vue (the insight
           "Visualization" detail tab). It draws its own card (selfChrome).
    CONTRACT:
      props:
        modelValue (Object, req) — { insight_type, viz_type, viz_config, layout,
                                     display_order }
        outputColumns (Array)    — the insight's output column names
      emits: update:modelValue
    NOTES: [reused verbatim from v1] shared component — do NOT restyle without
           checking other callers. Gotchas: task insights (insight_type ==
           'task_insight') have NO visualization — viz_type/viz_config/layout are
           ignored and consumed by task forms via /api/returnmetricinsight/;
           per-viz-type state is hydrated from viz_config and re-serialized on
           change; the Advanced JSON is styling passthrough only and edits are NOT
           emitted while the JSON is invalid (avoids clobbering viz_config).
  -->
  <section class="bg-white border border-gray-200 rounded-2xl overflow-hidden">
    <div class="px-5 py-3.5 border-b border-gray-200 flex items-center justify-between">
      <span class="text-[13px] font-bold tracking-tight text-gray-800">{{ isTaskInsight ? 'Task Insight' : 'Visualization' }}</span>
      <div v-if="!isTaskInsight" class="flex items-center gap-1.5">
        <button
          v-for="v in vizTypes"
          :key="v"
          class="font-mono text-[11.5px] font-medium rounded-lg px-3 py-1.5 border"
          :style="cfg.viz_type === v
            ? { color:'#fff', background:'#7C3AED', borderColor:'#7C3AED' }
            : { color:'#4B5563', background:'#F9FAFB', borderColor:'#E5E7EB' }"
          @click="set('viz_type', v)"
        >{{ v }}</button>
      </div>
    </div>

    <!-- Task insights are per-patient clinical-context lookups consumed directly
         by specific Vue forms (CodeSuggestionWorkForm, Task_Review_Suspect/
         Encounter, Task_MedicationAdherence) via /api/returnmetricinsight/.
         There is no chart — viz_type/viz_config/layout are never read for these
         rows. Only the studio_config join (built above, in StudioJoinBuilder)
         and row-scope filtering matter. -->
    <div v-if="isTaskInsight" class="px-5 py-4.5">
      <p class="text-xs text-gray-500 leading-relaxed">
        Task insights have no visualization — they're per-patient data looked up directly by a
        task form (e.g. CodeSuggestionWorkForm, Task_MedicationAdherence) via
        <code class="font-mono text-[11px] bg-gray-100 rounded px-1 py-0.5">/api/returnmetricinsight/</code>.
        Configure the join above; row-scope filters (bene_mbi_id, tin, dx_code, etc.) are applied by the caller.
      </p>
      <div class="mt-3">
        <div class="text-[11px] font-medium text-gray-500 mb-1">Insight type</div>
        <div class="flex gap-1.5">
          <button
            v-for="t in insightTypes"
            :key="t"
            class="font-mono text-[11px] font-medium rounded-lg px-2.5 py-1.5 border"
            :style="cfg.insight_type === t
              ? { color:'#fff', background:'#2563EB', borderColor:'#2563EB' }
              : { color:'#4B5563', background:'#F9FAFB', borderColor:'#E5E7EB' }"
            @click="set('insight_type', t)"
          >{{ t }}</button>
        </div>
      </div>
    </div>

    <div v-else class="grid gap-5 px-5 py-4.5" style="grid-template-columns:minmax(0,1fr) clamp(280px, 30%, 360px);">
      <!-- ---------- FIELD MAPPING (viz_type specific) ---------- -->
      <div class="min-w-0">
        <div class="text-xs font-semibold text-gray-600 mb-2.5">Field Mapping</div>

        <!-- chart_v5 -->
        <div v-if="cfg.viz_type === 'chart_v5'" class="space-y-3.5">
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-[11px] font-medium text-gray-500 mb-1">Label field (x-axis)</label>
              <select v-model="chartV5.label_field" class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg" @change="emitVizConfig">
                <option :value="undefined">Select column...</option>
                <option v-for="c in outputColumns" :key="c" :value="c">{{ c }}</option>
              </select>
            </div>
          </div>

          <div>
            <div class="flex items-center justify-between mb-1.5">
              <label class="text-[11px] font-medium text-gray-500">Series</label>
              <button class="text-[11px] font-semibold text-blue-600 hover:text-blue-800" @click="addSeries">+ Add series</button>
            </div>
            <div v-if="!chartV5.series.length" class="text-[11px] text-gray-400 border border-dashed border-gray-200 rounded-lg py-3 text-center">
              No series yet.
            </div>
            <div v-for="(s, i) in chartV5.series" :key="i" class="border border-gray-200 rounded-lg p-2.5 mb-2">
              <div class="grid grid-cols-2 gap-2 mb-2">
                <select v-model="s.value_field" class="text-xs px-2 py-1.5 border border-gray-200 rounded-lg" @change="emitVizConfig">
                  <option :value="undefined">Value field...</option>
                  <option v-for="c in outputColumns" :key="c" :value="c">{{ c }}</option>
                </select>
                <input v-model="s.label" type="text" placeholder="Series label" class="text-xs px-2 py-1.5 border border-gray-200 rounded-lg" @input="emitVizConfig" />
              </div>
              <div class="grid grid-cols-3 gap-2">
                <select v-model="s.type" class="text-xs px-2 py-1.5 border border-gray-200 rounded-lg" @change="emitVizConfig">
                  <option value="bar">bar</option>
                  <option value="line">line</option>
                </select>
                <select v-model="s.y_axis" class="text-xs px-2 py-1.5 border border-gray-200 rounded-lg" @change="emitVizConfig">
                  <option value="left">left axis</option>
                  <option value="right">right axis</option>
                </select>
                <div class="flex items-center gap-1.5">
                  <input v-model="s.color" type="color" class="h-[30px] w-8 border border-gray-200 rounded-lg flex-shrink-0" @input="emitVizConfig" />
                  <button class="text-gray-300 hover:text-red-500 text-sm" @click="removeSeries(i)">×</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- table -->
        <div v-else-if="cfg.viz_type === 'table'" class="space-y-2">
          <label class="block text-[11px] font-medium text-gray-500 mb-1">Columns to show</label>
          <div v-if="!outputColumns.length" class="text-[11px] text-gray-400">No output columns yet.</div>
          <div v-for="c in outputColumns" :key="c" class="flex items-center gap-2.5 py-1">
            <input
              type="checkbox"
              :checked="isColumnShown(c)"
              class="h-3.5 w-3.5 text-primary-600 rounded"
              @change="toggleColumn(c)"
            />
            <span class="font-mono text-xs text-gray-700 w-40 flex-shrink-0">{{ c }}</span>
            <input
              v-if="isColumnShown(c)"
              type="text"
              :value="headerNameFor(c)"
              placeholder="Header label"
              class="flex-1 text-xs px-2 py-1 border border-gray-200 rounded-lg"
              @input="setHeaderName(c, $event.target.value)"
            />
          </div>
        </div>

        <!-- sankey -->
        <div v-else-if="cfg.viz_type === 'sankey'" class="space-y-3">
          <div class="grid grid-cols-3 gap-3">
            <div>
              <label class="block text-[11px] font-medium text-gray-500 mb-1">Source field</label>
              <select v-model="sankey.source_field" class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg" @change="emitVizConfig">
                <option :value="undefined">Select...</option>
                <option v-for="c in outputColumns" :key="c" :value="c">{{ c }}</option>
              </select>
            </div>
            <div>
              <label class="block text-[11px] font-medium text-gray-500 mb-1">Target field</label>
              <select v-model="sankey.target_field" class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg" @change="emitVizConfig">
                <option :value="undefined">Select...</option>
                <option v-for="c in outputColumns" :key="c" :value="c">{{ c }}</option>
              </select>
            </div>
            <div>
              <label class="block text-[11px] font-medium text-gray-500 mb-1">Value field</label>
              <select v-model="sankey.value_field" class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg" @change="emitVizConfig">
                <option :value="undefined">Select...</option>
                <option v-for="c in outputColumns" :key="c" :value="c">{{ c }}</option>
              </select>
            </div>
          </div>
          <div>
            <div class="flex items-center justify-between mb-1.5">
              <label class="text-[11px] font-medium text-gray-500">Stage labels (ordered)</label>
              <button class="text-[11px] font-semibold text-blue-600 hover:text-blue-800" @click="addStageLabel">+ Add stage</button>
            </div>
            <div v-if="!sankey.stage_labels.length" class="text-[11px] text-gray-400">No stages defined.</div>
            <div v-for="(label, i) in sankey.stage_labels" :key="i" class="flex items-center gap-1.5 mb-1.5">
              <span class="font-mono text-[10px] text-gray-400 w-4">{{ i + 1 }}</span>
              <input v-model="sankey.stage_labels[i]" type="text" class="flex-1 text-xs px-2 py-1 border border-gray-200 rounded-lg" @input="emitVizConfig" />
              <button class="text-gray-300 hover:text-red-500 text-sm" @click="removeStageLabel(i)">×</button>
            </div>
          </div>
        </div>

        <!-- heatmap -->
        <div v-else-if="cfg.viz_type === 'heatmap'" class="grid grid-cols-3 gap-3">
          <div>
            <label class="block text-[11px] font-medium text-gray-500 mb-1">X field</label>
            <select v-model="heatmap.x_field" class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg" @change="emitVizConfig">
              <option :value="undefined">Select...</option>
              <option v-for="c in outputColumns" :key="c" :value="c">{{ c }}</option>
            </select>
          </div>
          <div>
            <label class="block text-[11px] font-medium text-gray-500 mb-1">Y field</label>
            <select v-model="heatmap.y_field" class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg" @change="emitVizConfig">
              <option :value="undefined">Select...</option>
              <option v-for="c in outputColumns" :key="c" :value="c">{{ c }}</option>
            </select>
          </div>
          <div>
            <label class="block text-[11px] font-medium text-gray-500 mb-1">Value field</label>
            <select v-model="heatmap.value_field" class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg" @change="emitVizConfig">
              <option :value="undefined">Select...</option>
              <option v-for="c in outputColumns" :key="c" :value="c">{{ c }}</option>
            </select>
          </div>
        </div>

        <!-- pie -->
        <div v-else-if="cfg.viz_type === 'pie'" class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-[11px] font-medium text-gray-500 mb-1">Label field (slice)</label>
            <select v-model="pie.label_field" class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg" @change="emitVizConfig">
              <option :value="undefined">Select...</option>
              <option v-for="c in outputColumns" :key="c" :value="c">{{ c }}</option>
            </select>
          </div>
          <div>
            <label class="block text-[11px] font-medium text-gray-500 mb-1">Value field</label>
            <select v-model="pie.value_field" class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg" @change="emitVizConfig">
              <option :value="undefined">Select...</option>
              <option v-for="c in outputColumns" :key="c" :value="c">{{ c }}</option>
            </select>
          </div>
        </div>
      </div>

      <!-- ---------- LAYOUT + ADVANCED (sidebar) ---------- -->
      <div class="min-w-0 border-l border-gray-100 pl-5">
        <div class="text-xs font-semibold text-gray-600 mb-2.5">Layout</div>
        <div>
          <div class="text-xs font-semibold text-gray-600 mb-2">Insight type</div>
          <div class="flex gap-1.5 mb-3.5">
            <button
              v-for="t in insightTypes"
              :key="t"
              class="font-mono text-[11px] font-medium rounded-lg px-2.5 py-1.5 border"
              :style="cfg.insight_type === t
                ? { color:'#fff', background:'#2563EB', borderColor:'#2563EB' }
                : { color:'#4B5563', background:'#F9FAFB', borderColor:'#E5E7EB' }"
              @click="set('insight_type', t)"
            >{{ t }}</button>
          </div>
        </div>
        <div class="grid grid-cols-2 gap-3 mb-4">
          <div>
            <div class="text-[11px] font-medium text-gray-500 mb-1.5">Width</div>
            <select
              :value="(cfg.layout && cfg.layout.width) || 'full'"
              class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg"
              @change="setLayout('width', $event.target.value)"
            >
              <option value="full">full</option>
              <option value="half">half</option>
            </select>
          </div>
          <div>
            <div class="text-[11px] font-medium text-gray-500 mb-1.5">Display order</div>
            <input
              type="number"
              :value="cfg.display_order || 0"
              class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg"
              @input="set('display_order', Number($event.target.value))"
            />
          </div>
        </div>

        <button
          class="text-[11px] font-semibold text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-2"
          @click="advancedOpen = !advancedOpen"
        >
          <span class="text-[9px]">{{ advancedOpen ? '▾' : '▸' }}</span>
          Advanced (raw chart_config JSON)
        </button>
        <div v-if="advancedOpen">
          <div class="flex items-center justify-between mb-1">
            <span class="text-[10px] text-gray-400">Styling passthrough — title, axes, legend, margins</span>
            <span v-if="jsonError" class="text-[10px] text-red-500">invalid JSON</span>
          </div>
          <textarea
            v-model="rawConfigText"
            rows="10"
            class="w-full font-mono text-[10.5px] leading-relaxed text-gray-800 bg-gray-50 border rounded-lg px-2.5 py-2 resize-y focus:outline-none"
            :style="{ borderColor: jsonError ? '#FCA5A5' : '#E5E7EB' }"
            @input="onRawConfigInput"
          ></textarea>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
// viz_config field-mapping editor. Builds the exact schema metricInsightAdapter.js
// expects per viz_type, from the insight's real output columns — no blind JSON.
export default {
  name: 'InsightVizPanel',
  props: {
    // v-model: { insight_type, viz_type, viz_config, layout, display_order }
    modelValue: { type: Object, required: true },
    outputColumns: { type: Array, default: () => [] },
  },
  emits: ['update:modelValue'],
  data() {
    const vc = this.modelValue.viz_config || {};
    return {
      insightTypes: ['metric_insight', 'task_insight'],
      vizTypes: ['chart_v5', 'table', 'sankey', 'heatmap', 'pie'],
      cfg: { ...this.modelValue },
      advancedOpen: false,
      jsonError: false,

      // Per-viz-type structured state, hydrated from viz_config and re-serialized on change.
      chartV5: {
        label_field: vc.label_field,
        series: (vc.series || []).map((s) => ({ ...s })),
      },
      tableColumnDefs: (vc.columnDefs || []).map((c) => ({ ...c })),
      sankey: {
        source_field: vc.source_field,
        target_field: vc.target_field,
        value_field: vc.value_field,
        stage_labels: [...(vc.stage_labels || [])],
      },
      heatmap: {
        x_field: vc.x_field,
        y_field: vc.y_field,
        value_field: vc.value_field,
      },
      pie: {
        label_field: vc.label_field,
        value_field: vc.value_field,
      },
      // Passthrough styling config kept as raw JSON (chart_config / config / colors).
      rawConfigText: JSON.stringify(this.passthroughFor(vc, this.modelValue.viz_type), null, 2),
    };
  },
  watch: {
    modelValue(v) {
      this.cfg = { ...v };
      const vc = v.viz_config || {};
      this.chartV5 = { label_field: vc.label_field, series: (vc.series || []).map((s) => ({ ...s })) };
      this.tableColumnDefs = (vc.columnDefs || []).map((c) => ({ ...c }));
      this.sankey = {
        source_field: vc.source_field, target_field: vc.target_field,
        value_field: vc.value_field, stage_labels: [...(vc.stage_labels || [])],
      };
      this.heatmap = { x_field: vc.x_field, y_field: vc.y_field, value_field: vc.value_field };
      this.pie = { label_field: vc.label_field, value_field: vc.value_field };
      this.rawConfigText = JSON.stringify(this.passthroughFor(vc, v.viz_type), null, 2);
      this.jsonError = false;
    },
  },
  computed: {
    isTaskInsight() {
      return this.cfg.insight_type === 'task_insight';
    },
  },
  methods: {
    passthroughFor(vc, vizType) {
      // The non-field-mapping keys each builder reads (styling passthrough only).
      if (vizType === 'chart_v5') return vc.chart_config || {};
      if (vizType === 'sankey') return vc.config || {};
      if (vizType === 'heatmap') return vc.chart_config || {};
      if (vizType === 'pie') return { chart_config: vc.chart_config || {}, colors: vc.colors || null };
      return {};
    },
    buildVizConfig() {
      const passthrough = (() => {
        try { return JSON.parse(this.rawConfigText || '{}'); } catch { return null; }
      })();
      if (passthrough === null) return this.cfg.viz_config; // don't clobber while advanced JSON is invalid

      switch (this.cfg.viz_type) {
        case 'chart_v5':
          return { label_field: this.chartV5.label_field, series: this.chartV5.series, chart_config: passthrough };
        case 'table':
          return { columnDefs: this.tableColumnDefs };
        case 'sankey':
          return { ...this.sankey, config: passthrough };
        case 'heatmap':
          return { ...this.heatmap, chart_config: passthrough };
        case 'pie':
          return { label_field: this.pie.label_field, value_field: this.pie.value_field, ...passthrough };
        default:
          return this.cfg.viz_config;
      }
    },
    emitVizConfig() {
      const viz_config = this.buildVizConfig();
      this.cfg = { ...this.cfg, viz_config };
      this.emit();
    },
    set(key, val) {
      this.cfg = { ...this.cfg, [key]: val };
      if (key === 'viz_type') {
        // Re-derive viz_config for the new type from currently-mapped fields (best effort).
        this.$nextTick(() => this.emitVizConfig());
        return;
      }
      this.emit();
    },
    setLayout(key, val) {
      this.cfg = { ...this.cfg, layout: { ...(this.cfg.layout || {}), [key]: val } };
      this.emit();
    },
    onRawConfigInput() {
      try {
        JSON.parse(this.rawConfigText || '{}');
        this.jsonError = false;
        this.emitVizConfig();
      } catch {
        this.jsonError = true; // don't emit while invalid
      }
    },

    // chart_v5 series
    addSeries() {
      this.chartV5.series.push({ value_field: undefined, label: '', color: '#60B5FF', type: 'bar', y_axis: 'left' });
      this.emitVizConfig();
    },
    removeSeries(i) {
      this.chartV5.series.splice(i, 1);
      this.emitVizConfig();
    },

    // table columns
    isColumnShown(field) {
      return this.tableColumnDefs.some((c) => c.field === field);
    },
    headerNameFor(field) {
      const c = this.tableColumnDefs.find((c) => c.field === field);
      return c ? (c.headerName || '') : '';
    },
    toggleColumn(field) {
      const idx = this.tableColumnDefs.findIndex((c) => c.field === field);
      if (idx >= 0) this.tableColumnDefs.splice(idx, 1);
      else this.tableColumnDefs.push({ field, headerName: field });
      this.emitVizConfig();
    },
    setHeaderName(field, val) {
      const c = this.tableColumnDefs.find((c) => c.field === field);
      if (c) { c.headerName = val; this.emitVizConfig(); }
    },

    // sankey stage labels
    addStageLabel() {
      this.sankey.stage_labels.push('');
      this.emitVizConfig();
    },
    removeStageLabel(i) {
      this.sankey.stage_labels.splice(i, 1);
      this.emitVizConfig();
    },
  },
};
</script>
